import * as React from "react";
import dynamic from "next/dynamic";
import Head from "next/head";
import { LandingPage } from "@/components/lp/LandingPage";
import { SignInModal } from "@/components/auth/SignInModal";
import { authPromise } from "@/lib/auth";

const PaymentStep = dynamic(
  () => import("@/components/payment/PaymentStep").then((m) => ({ default: m.PaymentStep })),
  { ssr: false },
);

const PhoneVerificationStep = dynamic(
  () => import("@/components/phone-verification/PhoneVerification").then((m) => ({ default: m.PhoneVerification })),
  { ssr: false },
);

const SetupWhatsApp = dynamic(
  () => import("@/components/workspace/SetupWhatsApp").then((m) => ({ default: m.SetupWhatsApp })),
  { ssr: false },
);

type Step = "landing" | "payment" | "phoneVerification" | "setup";

function determineStepForLoggedInUser(auth: {
  getUserDetails: () => { isPremium: boolean; verifiedPhoneNumber: string | null } | null;
  getUserSubscription: () => { activePlan: string | null } | null;
}): Step {
  const details = auth.getUserDetails();
  if (!details) return "payment";

  const hasActiveSub = details.isPremium || auth.getUserSubscription()?.activePlan != null;

  if (hasActiveSub && details.verifiedPhoneNumber) return "setup";
  if (hasActiveSub) return "phoneVerification";
  return "payment";
}

const HomePage: React.FC = () => {
  const [step, setStep] = React.useState<Step>("landing");
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [isReady, setIsReady] = React.useState(false);

  React.useEffect(() => {
    authPromise.then((auth) => {
      if (auth?.isLoggedIn()) {
        setStep(determineStepForLoggedInUser(auth));
      }
      setIsReady(true);
    });
  }, []);

  const handleCtaClick = () => {
    authPromise.then((auth) => {
      if (auth?.isLoggedIn()) {
        setStep(determineStepForLoggedInUser(auth));
      } else {
        setIsModalOpen(true);
      }
    });
  };

  const handleAuthSuccess = () => {
    setIsModalOpen(false);
    authPromise.then((auth) => {
      if (auth) {
        setStep(determineStepForLoggedInUser(auth));
      }
    });
  };

  if (!isReady) return null;

  return (
    <>
      <Head>
        <title>WhatsApp Reminders by Any.do - Never Forget a Task Again</title>
        <meta
          name="description"
          content="Get reminders directly in WhatsApp. No app switching, no forgotten tasks – just message and done."
        />
      </Head>

      {step === "landing" && (
        <>
          <LandingPage onCtaClick={handleCtaClick} />
          <SignInModal open={isModalOpen} onOpenChange={setIsModalOpen} onSuccess={handleAuthSuccess} />
        </>
      )}

      {step === "payment" && <PaymentStep onSuccess={() => setStep("phoneVerification")} />}

      {step === "phoneVerification" && <PhoneVerificationStep onSuccess={() => setStep("setup")} />}

      {step === "setup" && <SetupWhatsApp />}
    </>
  );
};

export default HomePage;
